/*
 * Lemonade Training Oy, copy rights, all right reserved.
 */

/** One capped list per uid; the banner writes it. */
export const ACCESS_NOTICE_DISMISSED_KEY_PREFIX = "lemonade-access-notice-dismissed:";

const DISMISSED_LIMIT = 20;

const storageKey = (uid: string): string => `${ACCESS_NOTICE_DISMISSED_KEY_PREFIX}${uid}`;

const getStore = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * Scoped per uid so a shared gym machine never shows one person's dismissals
 * to the next.
 */
export const readDismissedNotices = (uid: string | null | undefined): string[] => {
  if (!uid) return [];
  try {
    const raw = getStore()?.getItem(storageKey(uid));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
};

export const addDismissedNotice = (uid: string | null | undefined, noticeID: string): void => {
  if (!uid || !noticeID) return;
  try {
    const store = getStore();
    if (!store) return;
    const existing = readDismissedNotices(uid);
    if (existing.includes(noticeID)) return;
    const next = [...existing, noticeID].slice(-DISMISSED_LIMIT);
    store.setItem(storageKey(uid), JSON.stringify(next));
  } catch {
    // A blocked store costs a banner that reappears until readAt lands; it
    // must never break dismissal itself.
  }
};

/**
 * Swept on sign-out: each entry names a notice about one person's gym access,
 * and that is their data sitting in an unencrypted store after their session.
 */
export const clearAccessNoticeDismissals = (): void => {
  try {
    const store = getStore();
    if (!store) return;
    const keys: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key?.startsWith(ACCESS_NOTICE_DISMISSED_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => store.removeItem(key));
  } catch {
    // A blocked or unavailable store must never break sign-out.
  }
};
